/**
 * The broker's reconnect catch-up path (`fetch-events`) delivers a backlog of
 * events batched as arrays, on the same event names used for live, single-event
 * delivery (`message`, `reaction`, `chat`, `chat-member`, `member`). This wraps
 * a single-item handler so it transparently accepts either shape:
 *
 *   - a lone event object is handled as-is (live path), and
 *   - an array is fanned out item-by-item, in order (catch-up path).
 *
 * Items are applied sequentially so ordering within a batch is preserved (e.g.
 * a message edit never lands before its create). A failure on one item is
 * logged and skipped rather than aborting the rest of the batch — mirroring the
 * live path, where each event arrives as an independent emit.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { createLogger } from "@/utils/debug";

const log = createLogger("Socket");

export function fanOutBatched<T>(
  eventName: string,
  handler: (item: T) => any,
): (data: T | T[]) => Promise<void> {
  return async (data: T | T[]) => {
    if (!Array.isArray(data)) {
      await handler(data);
      return;
    }
    for (const item of data) {
      try {
        await handler(item);
      } catch (error) {
        log.error(`Error handling batched "${eventName}" item`, error);
      }
    }
  };
}
